// portraitArt.tsx — procedural placeholder 立ち絵 (flow.md l.509: MVP placeholder art; note/booth art
// swaps in later via portraits.ts `art.ref` without changing the id). Pure inline SVG: zero assets, zero
// xAI spend, deterministic per portrait id so every 門番/CPU/福引景品 looks distinct & intentional —
// an elegant band-tinted silhouette "awaiting its real 立ち絵", NOT a broken blob.
//
// resolvePortraitArt() is the single seam the home / 福引 / 段位戦CPU all render through: when real art
// lands (source !== 'placeholder'), it returns a {kind:'img', src} and callers swap to an <img> — the
// id (collection/CPU/gacha key) never changes.
import type { CSSProperties } from 'react';
import { fnv1a32 } from '@mj/protocol';
import type { Portrait, DanBand, PortraitArt } from './portraits';

type Pal = { robe: [string, string]; rim: string; bg: [string, string]; emblem: string };
const PALETTE: Record<DanBand, Pal> = {
  kyu: { robe: ['#2f9e90', '#16615a'], rim: '#9ff0e4', bg: ['#dff7f1', '#a9e2d8'], emblem: '#0c3b35' },
  jundan: { robe: ['#4f86d6', '#274f93'], rim: '#bcd6ff', bg: ['#e6f0ff', '#bcd3f4'], emblem: '#10294f' },
  jou: { robe: ['#8e6fd0', '#553a91'], rim: '#e2d2ff', bg: ['#f1ebff', '#cfbcf0'], emblem: '#2c1c52' },
  ryuu: { robe: ['#d05a55', '#8f2d2f'], rim: '#ffc9bf', bg: ['#ffe9e6', '#f3bdb7'], emblem: '#561314' },
  ou: { robe: ['#e0a93f', '#a9761d'], rim: '#ffe9af', bg: ['#fff3d6', '#f1d79a'], emblem: '#5b3c0b' },
  ten: { robe: ['#5b6b8c', '#2a3350'], rim: '#cdd6f0', bg: ['#edf0fa', '#c4cce4'], emblem: '#1a2138' },
};

function seed(id: string): number {
  return parseInt(fnv1a32(id).slice(0, 8), 16) >>> 0;
}

/** Display-ready resolution of a portrait's art. Placeholder → procedural SVG; real → an <img> src. */
export function resolvePortraitArt(art: PortraitArt): { kind: 'svg' } | { kind: 'img'; src: string } {
  return art.source === 'placeholder' ? { kind: 'svg' } : { kind: 'img', src: art.ref };
}

export function ProceduralPortrait({
  portrait,
  className,
  style,
  showEmblem = true,
}: {
  portrait: Portrait;
  className?: string;
  style?: CSSProperties;
  showEmblem?: boolean;
}) {
  const p = PALETTE[portrait.band];
  const h = seed(portrait.id);
  const hair = h % 4; // 4 silhouette crowns
  const tilt = ((h >> 3) % 5) - 2; // -2..2 deg lean
  const emblemCh = portrait.name.ja[0] ?? '雀';
  const uid = portrait.id.replace(/[^a-z0-9]/gi, '');
  const gRobe = `r${uid}`, gBg = `b${uid}`, gHalo = `h${uid}`;

  // silhouette crowns (hair) — top edge of the head varies by id
  const crowns = [
    'M96 96 q54 -64 108 0 q-12 -30 -54 -30 q-42 0 -54 30 Z',
    'M92 100 q58 -78 116 0 q-8 -44 -28 -40 q-30 -22 -60 0 q-20 -4 -28 40 Z',
    'M98 92 q52 -54 104 0 q-6 -26 -30 -34 l-10 14 -10 -16 -12 16 -10 -14 q-24 8 -22 34 Z',
    'M94 102 q60 -86 112 0 q2 -50 -34 -52 q-22 14 -44 0 q-36 6 -34 52 Z',
  ];

  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 300 400"
      role="img"
      aria-label={`${portrait.name.ja}（立ち絵 準備中）`}
      preserveAspectRatio="xMidYMax meet"
    >
      <defs>
        <linearGradient id={gRobe} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={p.robe[0]} />
          <stop offset="1" stopColor={p.robe[1]} />
        </linearGradient>
        <radialGradient id={gBg} cx="0.5" cy="0.42" r="0.7">
          <stop offset="0" stopColor={p.bg[0]} />
          <stop offset="1" stopColor={p.bg[1]} />
        </radialGradient>
        <radialGradient id={gHalo} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#fff" stopOpacity="0.85" />
          <stop offset="0.7" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="300" height="400" rx="18" fill={`url(#${gBg})`} />
      <circle cx="150" cy="150" r="120" fill={`url(#${gHalo})`} />

      <g transform={`rotate(${tilt} 150 220)`}>
        {/* robe / haori bust */}
        <path
          d="M150 196 L96 232 L70 400 L230 400 L204 232 Z"
          fill={`url(#${gRobe})`}
          stroke={p.rim}
          strokeWidth="3"
          strokeOpacity="0.7"
        />
        {/* V-neck shadow */}
        <path d="M150 196 L132 250 L150 286 L168 250 Z" fill={p.robe[1]} fillOpacity="0.65" />
        {/* neck */}
        <rect x="138" y="176" width="24" height="34" rx="10" fill={p.robe[1]} />
        {/* head silhouette */}
        <ellipse cx="150" cy="138" rx="44" ry="50" fill={p.robe[1]} />
        <ellipse cx="150" cy="138" rx="44" ry="50" fill="none" stroke={p.rim} strokeWidth="2.5" strokeOpacity="0.6" />
        {/* hair crown */}
        <path d={crowns[hair]} fill={p.robe[1]} />
        {/* 家紋 emblem on chest */}
        {showEmblem && (
          <g>
            <circle cx="150" cy="318" r="30" fill="#fff" fillOpacity="0.16" stroke={p.rim} strokeWidth="2" strokeOpacity="0.7" />
            <text
              x="150"
              y="318"
              textAnchor="middle"
              dominantBaseline="central"
              fontFamily="var(--font-display, serif)"
              fontSize="30"
              fontWeight="700"
              fill="#fff"
              fillOpacity="0.92"
            >
              {emblemCh}
            </text>
          </g>
        )}
      </g>
    </svg>
  );
}
